import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { LANG_PREFIXES, stripLangPrefix } from './LangWrapper';

const SITE = 'https://cctvplanner.io';

/** All languages including English */
const ALL_LANGS = ['en', ...LANG_PREFIXES] as const;

/** Convert internal lang code to hreflang attribute value */
function toHreflang(lang: string): string {
  if (lang === 'pt-br') return 'pt-BR';
  if (lang === 'pt-pt') return 'pt-PT';
  return lang;
}

const ogLocaleMap: Record<string, string> = {
  en: 'en_US', pl: 'pl_PL', de: 'de_DE', es: 'es_ES',
  'pt-br': 'pt_BR', 'pt-pt': 'pt_PT', hi: 'hi_IN', id: 'id_ID', fil: 'fil_PH',
  tr: 'tr_TR', ar: 'ar_SA', vi: 'vi_VN', th: 'th_TH',
  ja: 'ja_JP', ko: 'ko_KR', fr: 'fr_FR', it: 'it_IT',
  uk: 'uk_UA', cs: 'cs_CZ', nl: 'nl_NL', hu: 'hu_HU', lt: 'lt_LT', ro: 'ro_RO', sv: 'sv_SE',
};

/**
 * Renders <Helmet> with hreflang, canonical, og:locale, and html lang
 * for the current page across all supported languages.
 *
 * Uses react-helmet-async so tags are captured during SSG (not useEffect).
 * Placed in RootLayout so it runs on every page.
 */
export default function HreflangTags() {
  const location = useLocation();
  // Strip lang prefix AND trailing slashes so all URLs are normalized
  // (matches the post-process canonical format)
  const basePath = stripLangPrefix(location.pathname).replace(/\/+$/, '');

  // Don't add hreflang for protected/non-translatable routes.
  // Match on exact path or "prefix + /" so "/cctv-design" does NOT also
  // skip sibling SEO landings like "/cctv-design-tool" / "-software".
  // /compare/sku/ is EN-only programmatic SKU comparison (Day-3 sprint) — no per-lang
  // routes exist, so emitting hreflang would point Google at 404s.
  // /nvr/ is EN-only too (route is the bare `nvr/:slug`, canonical points at the EN
  // URL, content is English). Without this skip, HreflangTags emitted 22 cross-language
  // alternates per NVR (e.g. /ja/nvr/<model>) that have no route → SPA-fallback NotFound
  // → noindex, which Google reported under "Excluded by 'noindex' tag".
  // /p/ (per-customer Portal share links) and /unsubscribed (email utility) are
  // private/non-SEO and have no per-language routes — never emit hreflang for them.
  const skipPrefixes = ['/auth', '/dashboard', '/cctv-design', '/checkout-success', '/api-docs', '/camera/', '/nvr/', '/compare/sku/', '/p/', '/unsubscribed'];
  if (skipPrefixes.some(p => basePath === p || basePath.startsWith(p.endsWith('/') ? p : p + '/'))) {
    return null;
  }

  // Detect current language from URL (handles both /es and /es/)
  const currentLangPrefix = LANG_PREFIXES.find(
    p => location.pathname.startsWith(`/${p}/`) || location.pathname === `/${p}` || location.pathname === `/${p}/`
  );
  const currentLang = currentLangPrefix || 'en';

  // Locale-specific city-page URL patterns. These pages exist ONLY in their
  // native-language URL variant (e.g. /pl/projekt-cctv-warszawa exists but
  // /de/projekt-cctv-warszawa does NOT — DE Warsaw would be /de/cctv-planung-warszawa
  // which we also don't generate because Warsaw is in PL_CITIES only).
  //
  // Emitting cross-language hreflang for these pages was sending Google to
  // 200+ non-existent URLs (each city × each lang prefix), which the SPA
  // fallback served as 404 + noindex. GSC then flagged the whole set as
  // "Excluded by 'noindex' tag" (validation failed 2026-05-19).
  //
  // Fix: detect city-page URL patterns and emit canonical + self-referencing
  // hreflang only, no cross-language alternates. Per Google docs: "If you don't
  // have a version for that page in another language, don't add hreflang for it."
  const localizedCityPatterns = [
    /^\/cctv-design-/,         // EN cities (root-level)
    /^\/projekt-cctv-/,        // PL
    /^\/cctv-planung-/,        // DE
    /^\/progettazione-cctv-/,  // IT
    /^\/diseno-cctv-/,         // ES
    /^\/navrh-cctv-/,          // CS
    /^\/projeto-cftv-/,        // PT-BR
    /^\/cctv-ontwerp-/,        // NL
    /^\/cctv-tervezes-/,       // HU
    /^\/cctv-projektavimas-/,  // LT
    /^\/proiectare-cctv-/,     // RO
  ];
  // `/^\/cctv-design-/` also matches the real multi-language product pages
  // /cctv-design-tool and /cctv-design-software (translated into every locale),
  // which must keep their full cross-language hreflang. Exclude them so only
  // genuine single-language city landings (/cctv-design-london, …) are treated
  // as self-only.
  const cityPatternExceptions = ['/cctv-design-tool', '/cctv-design-software'];
  // Real, indexable, English-only pages with no per-language route (e.g. the
  // LLM-citation fact sheet, the compliance hub). They keep a self-canonical but
  // must emit self-only hreflang — full 22-lang would advertise /{lang}/<path>
  // URLs that 404 → noindex (same class as the NVR leak, micro-scale).
  const enOnlySinglePages = ['/about/facts', '/compliance'];
  const isLocalizedSinglePage = enOnlySinglePages.includes(basePath)
    || (!cityPatternExceptions.includes(basePath) && localizedCityPatterns.some(p => p.test(basePath)));

  // Build hreflang entries (no trailing slash, consistent with canonical).
  // Single-language pages get a self-reference only; cross-language pages
  // get the full ALL_LANGS set.
  const hreflangs = isLocalizedSinglePage
    ? [{
        hreflang: toHreflang(currentLang),
        href: currentLang === 'en'
          ? (basePath ? `${SITE}${basePath}` : SITE)
          : `${SITE}/${currentLang}${basePath}`,
      }]
    : ALL_LANGS.map(lang => ({
        hreflang: toHreflang(lang),
        href: lang === 'en'
          ? (basePath ? `${SITE}${basePath}` : SITE)
          : `${SITE}/${lang}${basePath}`,
      }));

  // Self-canonical: every page (including localized variants) points to
  // itself so Google indexes each language version instead of treating
  // non-English URLs as duplicates of the English root.
  const canonicalHref = currentLang === 'en'
    ? (basePath ? `${SITE}${basePath}` : SITE)
    : `${SITE}/${currentLang}${basePath}`;

  return (
    <Helmet>
      {/* BCP-47 compliant html lang — pt-br → pt-BR, pt-pt → pt-PT, rest unchanged */}
      <html lang={toHreflang(currentLang)} dir={currentLang === 'ar' ? 'rtl' : 'ltr'} />
        <meta property="og:locale" content={ogLocaleMap[currentLang] || 'en_US'} />
      <link rel="canonical" href={canonicalHref} />
      <meta property="og:url" content={canonicalHref} />
      {/* Hreflang alternates — full set for translatable pages, self-ref only
          for locale-specific single-language pages (city landings). */}
      {hreflangs.map(({ hreflang, href }) => (
        <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
      ))}
      {/* x-default + og:locale:alternate only for pages with real cross-language
          alternates. Single-language city pages don't have a "default" — the
          one URL that exists is both default and the only language version. */}
      {!isLocalizedSinglePage && (
        <link rel="alternate" hrefLang="x-default" href={basePath ? `${SITE}${basePath}` : SITE} />
      )}
      {!isLocalizedSinglePage && ALL_LANGS.filter(l => l !== currentLang).map(lang => (
        <meta key={lang} property="og:locale:alternate" content={ogLocaleMap[lang] || lang} />
      ))}
    </Helmet>
  );
}
